import { ease } from "./easing.ts";
import { lookOf, transitionProgress } from "./fx.ts";
import { msToFrame } from "./time.ts";
import type { AnimProperty, CameraElement, DrawableElement, Look, Project, Scene, SubtitleStyle, Track, Transform } from "./types.ts";

export interface ObjectState { id: string; element: DrawableElement; t: Transform; mediaTimeMs?: number }
export interface CameraState { fov: number; distance: number; x: number; y: number; z: number; roll: number; pitch: number; yaw: number }
export interface SceneState { scene: Scene; localMs: number; camera: CameraState; objects: ObjectState[]; transitionU: number | null }
export interface CaptionState { text: string; style: SubtitleStyle }
export interface FrameState { time_ms: number; frame: number; width: number; height: number; scenes: SceneState[]; caption: CaptionState | null; look: Look }

/** HTML elements are redrawn on a 60 Hz grid of their scene-local clock: enough for smooth motion, and a bounded number of distinct pictures. */
export const htmlClock = (localMs: number): number => Math.round((localMs * 60) / 1000) * (1000 / 60);

export const DEFAULT_CAMERA: CameraState = { fov: 35, distance: 1, x: 0, y: 0, z: 0, roll: 0, pitch: 0, yaw: 0 };

/** Value of a keyframe track at a scene-local time. Holds the first/last value outside the range;
 *  the easing of keyframe i shapes the segment i -> i+1. */
export function sampleTrack(track: Track, localMs: number): number {
  const ks = track.keyframes;
  const first = ks[0], last = ks[ks.length - 1];
  if (!first || !last) throw new Error("track without keyframes");
  if (localMs <= first.time_ms) return first.value;
  const span = last.time_ms - first.time_ms;
  if (localMs >= last.time_ms) {
    if (!track.loop || track.loop === "none" || span <= 0) return last.value;
    // Anime.js-style loop: the keyframed span repeats (or plays back and forth) for as long as the scene lasts.
    const n = (localMs - first.time_ms) / span;
    const cycle = Math.floor(n);
    const frac = n - cycle;
    const reversed = track.loop === "pingpong" && cycle % 2 === 1;
    localMs = first.time_ms + span * (reversed ? 1 - frac : frac);
    if (frac === 0) return reversed ? last.value : (cycle === 0 ? last.value : first.value);
  }
  let lo = 0, hi = ks.length - 1;                    // binary search: ks[lo].time <= t < ks[hi].time
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if ((ks[mid] as { time_ms: number }).time_ms <= localMs) lo = mid; else hi = mid;
  }
  const a = ks[lo]!, b = ks[hi]!;
  const u = (localMs - a.time_ms) / (b.time_ms - a.time_ms);
  return a.value + (b.value - a.value) * ease(a.easing, u);
}

function tracksByElement(project: Project): Map<string, Track[]> {
  const m = new Map<string, Track[]>();
  for (const t of project.tracks) {
    const list = m.get(t.element_id);
    if (list) list.push(t); else m.set(t.element_id, [t]);
  }
  return m;
}

/** Pure function: same project + same time -> same state. No clocks, no randomness. */
export function evaluate(project: Project, timeMs: number): FrameState {
  const t = Math.min(Math.max(timeMs, 0), project.duration_ms);
  const byEl = tracksByElement(project);
  const scenes: SceneState[] = [];
  for (const scene of [...project.scenes].sort((a, b) => a.start_ms - b.start_ms)) {
    const end = scene.start_ms + scene.duration_ms;
    const active = t >= scene.start_ms && (t < end || (t === end && end === project.duration_ms));
    if (!active) continue;
    const local = t - scene.start_ms;
    const objects: ObjectState[] = [];
    let camera: CameraState = { ...DEFAULT_CAMERA };
    for (const id of scene.element_ids) {
      const el = project.elements[id];
      if (!el || !el.visible || el.type === "code") continue;      // code elements are drawn by the sandboxed player, not by this engine
      const values: Record<string, number> = { blur: 0, reveal: 1, draw: 1, morph: 0, ...el.transform } as unknown as Record<string, number>;
      let fov = el.type === "camera" ? el.fov : 0, distance = el.type === "camera" ? el.distance : 1;
      for (const tr of byEl.get(id) ?? []) {
        const v = sampleTrack(tr, local);
        const p: AnimProperty = tr.property;
        if (p === "fov") fov = v; else if (p === "distance") distance = v; else values[p] = v;
      }
      if (el.type === "camera") {
        const cam = el as CameraElement;
        camera = { fov: fov || cam.fov, distance, x: values.x!, y: values.y!, z: values.z!,
                   roll: values.rotation!, pitch: values.rotation_x!, yaw: values.rotation_y! };
      } else {
        if (el.type === "video") {
          if (local < el.start_ms || local >= el.start_ms + el.duration_ms) continue;
          objects.push({ id, element: el, t: values as unknown as Transform,
            mediaTimeMs: el.in_ms + (local - el.start_ms) * el.speed });
        } else if (el.type === "html") {
          objects.push({ id, element: el, t: values as unknown as Transform, mediaTimeMs: htmlClock(local) });   // element clock, on the 60 Hz grid
        } else objects.push({ id, element: el, t: values as unknown as Transform });
      }
    }
    const tr = scene.transition;
    scenes.push({ scene, localMs: local, camera, objects, transitionU: transitionProgress(tr?.kind, tr?.duration_ms ?? 0, local) });
  }
  return { time_ms: t, frame: msToFrame(t, project.fps), width: project.width, height: project.height, scenes, caption: captionAt(project, t), look: lookOf(project.look) };
}

/** The subtitle cue visible at time t (cues are [start, end) and never overlap), or null. */
export function captionAt(project: Project, t: number): CaptionState | null {
  const sub = project.subtitles;
  if (!sub || !sub.enabled) return null;
  const cue = sub.cues.find((c) => t >= c.start_ms && t < c.end_ms);
  return cue ? { text: cue.text, style: sub.style } : null;
}
